import React from "react";
import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import Form from "react-bootstrap/Form";
import { gettext } from "i18n";
import { useModalContext } from "state/modal";
import {
  ChartVisualSettings,
  TableVisualSettings,
  DEFAULT_CHART_VISUAL_SETTINGS,
  DEFAULT_TABLE_VISUAL_SETTINGS,
} from "utils/widgets";

export type VisualSettingsType = "chart" | "table" | "none";

interface WidgetSettingsModalProps {
  visualSettings: ChartVisualSettings | TableVisualSettings | undefined;
  visualSettingsType: VisualSettingsType;
  isMultiSource?: boolean;
  onSave: (settings: ChartVisualSettings | TableVisualSettings) => void;
}

const WidgetSettingsModal = ({
  visualSettings,
  visualSettingsType,
  isMultiSource = false,
  onSave,
}: WidgetSettingsModalProps) => {
  const { setActiveModal } = useModalContext();
  const onHide = () => setActiveModal(null);

  const defaultSettings =
    visualSettingsType === "chart"
      ? DEFAULT_CHART_VISUAL_SETTINGS
      : DEFAULT_TABLE_VISUAL_SETTINGS;

  const [editedSettings, setEditedSettings] = React.useState<
    ChartVisualSettings | TableVisualSettings
  >(visualSettings || defaultSettings);

  const handleSave = () => {
    onSave(editedSettings);
    onHide();
  };

  const isChartSettings = (
    settings: ChartVisualSettings | TableVisualSettings,
  ): settings is ChartVisualSettings => {
    return visualSettingsType === "chart";
  };

  const isTableSettings = (
    settings: ChartVisualSettings | TableVisualSettings,
  ): settings is TableVisualSettings => {
    return visualSettingsType === "table";
  };

  const updateSetting = <
    K extends keyof (ChartVisualSettings & TableVisualSettings),
  >(
    key: K,
    value: any,
  ) => {
    setEditedSettings((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  return (
    <Modal show onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>{gettext("Widget Settings")}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {visualSettingsType === "none" && (
          <p>{gettext("No visual settings available for this widget type.")}</p>
        )}

        {isChartSettings(editedSettings) && (
          <Form>
            {!isMultiSource && (
              <>
                <h6>{gettext("Appearance")}</h6>
                <Form.Group className="mb-3">
                  <Form.Label>{gettext("Font size")}</Form.Label>
                  <Form.Control
                    type="number"
                    min={8}
                    max={24}
                    value={editedSettings.fontSize}
                    onChange={(e) =>
                      updateSetting("fontSize", Number(e.target.value))
                    }
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>{gettext("Font color")}</Form.Label>
                  <Form.Control
                    type="color"
                    value={editedSettings.fontColor}
                    onChange={(e) => updateSetting("fontColor", e.target.value)}
                  />
                </Form.Group>
              </>
            )}
            {isMultiSource && (
              <p className="text-muted">
                {gettext(
                  "Font, line colors, and axis scaling can be configured per data source by clicking the settings icon next to each parameter title.",
                )}
              </p>
            )}
          </Form>
        )}

        {isTableSettings(editedSettings) && (
          <Form>
            <h6>{gettext("Appearance")}</h6>
            <Form.Group className="mb-3">
              <Form.Label>{gettext("Font size")}</Form.Label>
              <Form.Control
                type="number"
                min={8}
                max={24}
                value={editedSettings.fontSize}
                onChange={(e) =>
                  updateSetting("fontSize", Number(e.target.value))
                }
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>{gettext("Font color")}</Form.Label>
              <Form.Control
                type="color"
                value={editedSettings.fontColor}
                onChange={(e) => updateSetting("fontColor", e.target.value)}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>{gettext("Header font size")}</Form.Label>
              <Form.Control
                type="number"
                min={8}
                max={24}
                value={editedSettings.headerFontSize}
                onChange={(e) =>
                  updateSetting("headerFontSize", Number(e.target.value))
                }
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>{gettext("Header background color")}</Form.Label>
              <Form.Control
                type="color"
                value={editedSettings.headerBackgroundColor}
                onChange={(e) =>
                  updateSetting("headerBackgroundColor", e.target.value)
                }
              />
            </Form.Group>
          </Form>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          {gettext("Cancel")}
        </Button>
        <Button variant="primary" onClick={handleSave}>
          {gettext("Save Settings")}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default WidgetSettingsModal;
